import { Injectable } from '@angular/core';
import { HttpParams, RestService } from '../rest.service';
import { Tools } from '../helpers/tools';
import { environment } from 'src/environments/environment';
import { StatusCodes } from 'http-status-codes';
import { FILTER_NAME, searchApplication } from '../helpers/app-context-helper';
import { CustomerService } from '../customer.service';
import { logError } from '../helpers/sentry.helper';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class HbmApiService extends RestService {
  private refreshTokenPromise: Promise<void> | null = null;

  constructor(private readonly customer: CustomerService) {
    super();
  }

  private getAppFromContext(): {
    isCustomerOpened: boolean;
    appURL: string;
    origin: string;
    distribID: string;
  } {
    const ctx = this.customer.context;
    let app = environment.byMe[ctx];
    if (!app) {
      app = searchApplication(ctx, FILTER_NAME.DISTRIB_ID);

      if (!app) {
        logError('Contexte inconnu : ' + ctx);
      }
    }

    return app;
  }

  async acquireTokenWS(userID: string, service: 'token' | 'signup', preventLoop = false, emitEvent = true): Promise<void> {
    const data = {
      hostName: environment.byMe.apiHostName,
      service,
      body: {
        userID,
        client: this.getAppFromContext()?.distribID,
        tokenDuration: 14400000, // 4h
      }
    };

    try {
      const tokenResponse = await firstValueFrom(this.http.post<any>(environment.byMe.tokenURL, data));

      if (tokenResponse?.token) {
        this.customer.token = tokenResponse.token;
        this.customer.tokenInfos = tokenResponse;

        if (emitEvent) {
          this.customer.events.emit({
            type: 'CUSTOMER_TOKEN_ACQUIRED',
            data: {
              token: tokenResponse.token,
              distributionID: this.getAppFromContext()?.distribID,
              additionalInfo: {},
            },
          });
        }

        return;
      }

      if (!preventLoop && tokenResponse?.errorType) {
        // Maybe customer not already known by HBM, we try to create it
        await this.acquireTokenWS(userID, 'signup', true, emitEvent);
        return;
      }

      throw { code: 999, description: 'Erreur inconnue' };
    } catch (err) {
      logError(err);
      throw err;
    }
  }

  async acquireLegacyTokenWS(legacyCustomerID: string): Promise<void> {
    const data = {
      hostName: environment.byMe.apiHostName,
      service: 'token',
      body: {
        userID: legacyCustomerID,
        client: this.getAppFromContext()?.distribID,
        tokenDuration: 14400000, // 4h
      }
    };

    try {
      const tokenResponse = await firstValueFrom(this.http.post<any>(environment.byMe.tokenURL, data));

      if (tokenResponse?.token) {
        this.customer.legacyToken = tokenResponse.token;
        return;
      }

      throw new Error('no_customer_found');
    } catch (err) {
      logError(err);
      throw err;
    }
  }

  async callHbmApi(params: HttpParams): Promise<any> {
    try {
      return await this.makeApiCall(params);
    } catch (err) {
      logError(err);

      const isHttp4xx5xx =
        typeof err?.status === 'number' &&
        err.status >= StatusCodes.BAD_REQUEST &&
        err.status <= StatusCodes.INTERNAL_SERVER_ERROR;

      if (!isHttp4xx5xx) throw err;

      if (err?.error?.id === 'BM_005') {
        console.warn('token expiré');

        try {
          await this.refreshToken();
          return await this.makeApiCall(params);
        } catch (error_) {
          logError(error_);
          throw error_;
        }
      }
      
      throw err;
    }
  }

  private async makeApiCall(params: HttpParams): Promise<any> {
    return await firstValueFrom(this.restApi(params, {
      timestamp: Tools.generateFormatedTimestamp(),
      token: params.token ?? this.customer.token
    }));
  }

  private async refreshToken(): Promise<void> {
    if (!this.refreshTokenPromise) {
      const userId = this.customer.userDatas?.id;
      if (!userId) {
        throw new Error('Impossible de rafraîchir le token: user ID manquant');
      }

      this.refreshTokenPromise = this.acquireTokenWS(userId, 'token')
        .catch(err => {
          logError(err);
          throw err;
        })
        .finally(() => this.refreshTokenPromise = null);
    }

    return this.refreshTokenPromise;
  }
}
